import { Controller } from "@hotwired/stimulus"
import { errorDetail, formatDate, getAllJSON, getJSON, sendJSON } from "../api.js"

// Helpdesk: lista wątków + widok wątku + formularz nowego wątku (klient) / inbox (staff).
// Jedna strona, widoki przełączane `hidden`. Zero innerHTML — rendering przez <template>
// + textContent. Paginacja server-side (?page=), filtr statusu (staff) przez ?status=.
// „Zamknij wątek" dostępny z widoku wątku (klient i staff); z listy — tylko staff.
export default class extends Controller {
  static targets = [
    "listView", "threadView",
    "rows", "status", "pageInfo", "prevBtn", "nextBtn", "filterSelect",
    "reasonSelect", "orderSelect", "subjectInput", "bodyInput", "formError",
    "threadSubject", "threadMeta", "threadCloseBtn", "messages", "replyBody", "replyError",
  ]
  static values = {
    url: String,       // /api/helpdesk-threads/
    ordersUrl: String, // /api/orders/ (kontekst zamówienia, opcjonalny)
    staff: Boolean,
  }

  connect() {
    this.page = 1
    this.count = 0
    this.statusFilter = ""
    this.currentThreadId = null
    this.#loadDictionaries()
    this.loadList()
  }

  // --- lista wątków ---

  async loadList() {
    let data
    try {
      data = await getJSON(this.#listUrl())
    } catch {
      this.#status("Nie udało się załadować wątków. Odśwież stronę.")
      return
    }
    this.count = data.count ?? 0
    this.rowsTarget.textContent = ""
    if (!data.results?.length) {
      this.#status("Brak wątków.")
    } else {
      this.#status(null)
      const template = document.getElementById("helpdesk-thread-row-template")
      for (const thread of data.results) this.rowsTarget.appendChild(this.#buildRow(template, thread))
    }
    this.#updatePager(data)
  }

  prevPage() {
    if (this.page > 1) {
      this.page -= 1
      this.loadList()
    }
  }

  nextPage() {
    if (this.hasNext) {
      this.page += 1
      this.loadList()
    }
  }

  filter() {
    if (!this.hasFilterSelectTarget) return
    this.statusFilter = this.filterSelectTarget.value
    this.page = 1
    this.loadList()
  }

  // --- akcje staffu (inbox) ---

  async markRead(event) {
    const { id } = event.params
    const res = await sendJSON("POST", `${this.urlValue}${id}/mark-read/`)
    if (!res.ok) {
      this.#status(errorDetail(res.data, "Nie udało się oznaczyć wątku jako przeczytany."))
      return
    }
    await this.loadList()
  }

  async closeThread(event) {
    const { id } = event.params
    const res = await sendJSON("POST", `${this.urlValue}${id}/close/`)
    if (!res.ok) {
      this.#status(errorDetail(res.data, "Nie udało się zamknąć wątku."))
      return
    }
    await this.loadList()
  }

  // --- widok wątku ---

  async openThread(event) {
    const { id } = event.params
    this.currentThreadId = id
    let thread
    try {
      thread = await getJSON(`${this.urlValue}${id}/`)
    } catch {
      this.#status("Nie udało się otworzyć wątku.")
      return
    }
    if (this.hasThreadSubjectTarget) this.threadSubjectTarget.textContent = thread.subject
    if (this.hasThreadMetaTarget) {
      this.threadMetaTarget.textContent = `${thread.status_label} · ${thread.reason_name}`
    }
    if (this.hasThreadCloseBtnTarget) {
      this.threadCloseBtnTarget.hidden = thread.status === "closed"
    }
    await this.#loadMessages()
    this.listViewTarget.hidden = true
    this.threadViewTarget.hidden = false
  }

  backToList() {
    this.currentThreadId = null
    this.threadViewTarget.hidden = true
    this.listViewTarget.hidden = false
    this.loadList()
  }

  async reply() {
    if (this.currentThreadId === null) return
    const body = this.replyBodyTarget.value.trim()
    if (!body) {
      this.#replyError("Wpisz treść odpowiedzi.")
      return
    }
    const res = await sendJSON("POST", `${this.urlValue}${this.currentThreadId}/messages/`, { body })
    if (!res.ok) {
      this.#replyError(errorDetail(res.data, "Nie udało się wysłać odpowiedzi."))
      return
    }
    this.#replyError(null)
    this.replyBodyTarget.value = ""
    await this.#loadMessages()
  }

  async closeCurrentThread() {
    if (this.currentThreadId === null) return
    const res = await sendJSON("POST", `${this.urlValue}${this.currentThreadId}/close/`)
    if (!res.ok) {
      this.#replyError(errorDetail(res.data, "Nie udało się zamknąć wątku."))
      return
    }
    this.#replyError(null)
    let thread
    try {
      thread = await getJSON(`${this.urlValue}${this.currentThreadId}/`)
    } catch {
      this.#replyError("Nie udało się pobrać zaktualizowanego statusu wątku.")
      return
    }
    if (this.hasThreadMetaTarget) {
      this.threadMetaTarget.textContent = `${thread.status_label} · ${thread.reason_name}`
    }
    if (this.hasThreadCloseBtnTarget) {
      this.threadCloseBtnTarget.hidden = thread.status === "closed"
    }
  }

  // --- nowy wątek ---

  async create(event) {
    event.preventDefault()
    const reason = this.reasonSelectTarget.value
    if (!reason) {
      this.#formError("Wybierz powód kontaktu.")
      return
    }
    const subject = this.subjectInputTarget.value.trim()
    const body = this.bodyInputTarget.value.trim()
    if (!subject || !body) {
      this.#formError("Uzupełnij temat i treść wiadomości.")
      return
    }
    const payload = { subject, body, reason: Number(reason) }
    if (this.hasOrderSelectTarget && this.orderSelectTarget.value) {
      payload.order = Number(this.orderSelectTarget.value)
    }
    const res = await sendJSON("POST", this.urlValue, payload)
    if (!res.ok) {
      const fallback = res.status === 429
        ? "Przekroczono dzienny limit nowych wątków. Spróbuj ponownie jutro."
        : "Nie udało się utworzyć wątku."
      this.#formError(errorDetail(res.data, fallback))
      return
    }
    this.#formError(null)
    this.subjectInputTarget.value = ""
    this.bodyInputTarget.value = ""
    this.reasonSelectTarget.value = ""
    if (this.hasOrderSelectTarget) this.orderSelectTarget.value = ""
    this.page = 1
    await this.loadList()
  }

  // --- prywatne ---

  #listUrl() {
    const params = new URLSearchParams()
    if (this.page > 1) params.set("page", String(this.page))
    if (this.statusFilter) params.set("status", this.statusFilter)
    const query = params.toString()
    return query ? `${this.urlValue}?${query}` : this.urlValue
  }

  #buildRow(template, thread) {
    const row = template.content.firstElementChild.cloneNode(true)
    const badge = row.querySelector('[data-field="status"]')
    badge.textContent = thread.status_label
    badge.setAttribute("data-status", thread.status)
    row.querySelector('[data-field="reason"]').textContent = thread.reason_name
    row.querySelector('[data-field="subject"]').textContent = thread.subject
    row.querySelector('[data-field="last"]').textContent = formatDate(thread.last_message_at ?? thread.created)
    const openBtn = row.querySelector('[data-field="open"]')
    openBtn.setAttribute("data-helpdesk-id-param", thread.id)
    const readBtn = row.querySelector('[data-field="markRead"]')
    if (readBtn) {
      readBtn.setAttribute("data-helpdesk-id-param", thread.id)
      readBtn.hidden = !(this.staffValue && thread.status === "new")
    }
    const closeBtn = row.querySelector('[data-field="close"]')
    if (closeBtn) {
      closeBtn.setAttribute("data-helpdesk-id-param", thread.id)
      closeBtn.hidden = !(this.staffValue && thread.status !== "closed")
    }
    return row
  }

  async #loadMessages() {
    let messages
    try {
      messages = await getAllJSON(`${this.urlValue}${this.currentThreadId}/messages/`)
    } catch {
      this.#replyError("Nie udało się załadować wiadomości.")
      return
    }
    this.messagesTarget.textContent = ""
    const template = document.getElementById("helpdesk-message-template")
    for (const message of messages) {
      const node = template.content.firstElementChild.cloneNode(true)
      node.querySelector('[data-field="author"]').textContent = message.author_name || "—"
      node.querySelector('[data-field="created"]').textContent = formatDate(message.created)
      node.querySelector('[data-field="body"]').textContent = message.body
      if (message.is_staff_reply) node.classList.add("is-staff")
      this.messagesTarget.appendChild(node)
    }
  }

  async #loadDictionaries() {
    if (this.hasReasonSelectTarget) {
      try {
        const reasons = await getJSON(`${this.urlValue}reasons/`)
        for (const reason of reasons) {
          const option = document.createElement("option")
          option.value = reason.id
          option.textContent = reason.name
          this.reasonSelectTarget.appendChild(option)
        }
      } catch { /* słownik niedostępny — select zostaje pusty */ }
    }
    if (this.hasOrderSelectTarget && this.ordersUrlValue) {
      try {
        const orders = await getAllJSON(this.ordersUrlValue)
        for (const order of orders) {
          const option = document.createElement("option")
          option.value = order.id
          option.textContent = `Zamówienie #${order.id} · ${formatDate(order.created)}`
          this.orderSelectTarget.appendChild(option)
        }
      } catch { /* brak zamówień do podpięcia — pole zostaje puste */ }
    }
  }

  #updatePager(data) {
    this.hasNext = Boolean(data.next)
    if (this.hasPageInfoTarget) this.pageInfoTarget.textContent = `strona ${this.page} (${this.count})`
    if (this.hasPrevBtnTarget) this.prevBtnTarget.disabled = this.page <= 1
    if (this.hasNextBtnTarget) this.nextBtnTarget.disabled = !this.hasNext
  }

  #formError(message) {
    if (!this.hasFormErrorTarget) return
    this.formErrorTarget.hidden = message === null
    if (message !== null) this.formErrorTarget.textContent = message
  }

  #replyError(message) {
    if (!this.hasReplyErrorTarget) return
    this.replyErrorTarget.hidden = message === null
    if (message !== null) this.replyErrorTarget.textContent = message
  }

  #status(message) {
    if (!this.hasStatusTarget) return
    this.statusTarget.hidden = message === null
    if (message !== null) this.statusTarget.textContent = message
  }
}
